import React, { useEffect, useState } from 'react';
import CounterfindDashboardHeader from './CounterfindDashboardHeader';
import {
  ShieldCheck,
  Package,
  AlertTriangle,
  Gavel,
  DollarSign,
  Users,
  UserCheck,
  BarChart3,
  Briefcase,
  TrendingUp,
  Loader2,
} from 'lucide-react';

/**
 * Executive view of the Counterfind dashboard for counterfind@operatr.com.
 * Aggregates org-wide KPIs across every role (executive/operations/review/
 * sales/client). Backend endpoints accept `base_user_id` and query all
 * `{base}__cf_*` scopes plus the bare base id.
 */
const CounterfindExecutiveDashboard = ({ baseUserId }) => {
  const apiBase = window.location.origin;
  const [stats, setStats] = useState({
    total_listings_found: 0,
    confirmed_counterfeits: 0,
    takedowns_completed: 0,
    revenue: 0,
    employee_activity: 0,
    client_activity: 0,
  });
  const [reports, setReports] = useState({
    client_reports: [],
    team_performance: [],
    financial_summaries: [],
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      if (!baseUserId) return;
      setLoading(true);
      try {
        const [s, r] = await Promise.all([
          fetch(`${apiBase}/api/counterfind/executive/stats?base_user_id=${encodeURIComponent(baseUserId)}`).then(x => x.json()),
          fetch(`${apiBase}/api/counterfind/executive/reports?base_user_id=${encodeURIComponent(baseUserId)}`).then(x => x.json()),
        ]);
        setStats(s);
        setReports(r);
      } catch (e) {
        console.error('Counterfind executive fetch failed', e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, [baseUserId, apiBase]);

  const fmtCurrency = (n) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

  // Max helpers for bar widths
  const maxClient = Math.max(1, ...reports.client_reports.map(r => r.cases));
  const maxTeam = Math.max(1, ...reports.team_performance.map(r => r.cases));
  const maxRevenue = Math.max(1, ...reports.financial_summaries.map(r => r.revenue));

  return (
    <div className="min-h-screen bg-black text-white p-3 md:p-6" data-testid="counterfind-executive-dashboard">
      <div className="max-w-[1800px] mx-auto">
        <CounterfindDashboardHeader roleLabel="Executive" />
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck size={28} className="text-amber-400" />
            <h1 className="text-2xl md:text-3xl font-bold">Executive</h1>
          </div>
        </div>

        {/* KPI grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
          <ExecStat
            icon={Package}
            tone="amber"
            label="Total Listings Found"
            value={stats.total_listings_found}
            sub="All platforms, all roles"
            loading={loading}
            testId="kpi-total-listings"
          />
          <ExecStat
            icon={AlertTriangle}
            tone="rose"
            label="Confirmed Counterfeits"
            value={stats.confirmed_counterfeits}
            sub="Verified by our team"
            loading={loading}
            testId="kpi-confirmed-counterfeits"
          />
          <ExecStat
            icon={Gavel}
            tone="emerald"
            label="Takedowns Completed"
            value={stats.takedowns_completed}
            sub="Filed & enforced"
            loading={loading}
            testId="kpi-takedowns-completed-exec"
          />
          <ExecStat
            icon={DollarSign}
            tone="emerald"
            label="Revenue"
            value={fmtCurrency(stats.revenue)}
            sub="Generated from successful takedowns"
            loading={loading}
            testId="kpi-revenue"
          />
          <ExecStat
            icon={UserCheck}
            tone="cyan"
            label="Employee Activity"
            value={stats.employee_activity}
            sub="Internal cases · last 30 days"
            loading={loading}
            testId="kpi-employee-activity"
          />
          <ExecStat
            icon={Users}
            tone="purple"
            label="Client Activity"
            value={stats.client_activity}
            sub="Client submissions · last 30 days"
            loading={loading}
            testId="kpi-client-activity"
          />
        </div>

        {/* Reports container */}
        <div
          className="bg-gradient-to-br from-cyan-500/5 to-transparent border border-cyan-500/30 rounded-2xl p-5"
          data-testid="counterfind-executive-reports"
        >
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} className="text-cyan-400" />
            <h2 className="text-lg font-semibold">Reports</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Client Reports */}
            <div data-testid="exec-reports-client">
              <div className="flex items-center gap-2 mb-3">
                <Briefcase size={14} className="text-purple-400" />
                <span className="text-xs uppercase tracking-wider text-gray-400">Client Reports</span>
              </div>
              {reports.client_reports.length === 0 ? (
                <EmptyState text="No client cases yet." />
              ) : (
                <div className="space-y-2">
                  {reports.client_reports.map((c) => (
                    <div key={c.platform} className="text-sm">
                      <div className="flex items-center justify-between text-gray-300 mb-1">
                        <span className="truncate" title={c.platform}>{c.platform}</span>
                        <span className="text-xs text-gray-400">
                          {c.takedowns}/{c.cases} <span className="text-gray-600">Takedowns</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-900 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-purple-500 to-cyan-500"
                          style={{ width: `${(c.cases / maxClient) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Team Performance */}
            <div data-testid="exec-reports-team">
              <div className="flex items-center gap-2 mb-3">
                <UserCheck size={14} className="text-cyan-400" />
                <span className="text-xs uppercase tracking-wider text-gray-400">Team Performance</span>
              </div>
              {reports.team_performance.every(r => r.cases === 0) ? (
                <EmptyState text="No team activity yet." />
              ) : (
                <div className="space-y-2">
                  {reports.team_performance.map((t) => (
                    <div key={t.role} className="text-sm">
                      <div className="flex items-center justify-between text-gray-300 mb-1">
                        <span>{t.role}</span>
                        <span className="text-xs text-gray-400">
                          {t.takedowns}/{t.cases} <span className="text-gray-600">Takedowns</span>
                        </span>
                      </div>
                      <div className="h-1.5 bg-gray-900 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500"
                          style={{ width: `${(t.cases / maxTeam) * 100}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Financial Summaries */}
            <div data-testid="exec-reports-financial">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={14} className="text-emerald-400" />
                <span className="text-xs uppercase tracking-wider text-gray-400">Financial Summaries</span>
              </div>
              {reports.financial_summaries.length === 0 ? (
                <EmptyState text="No revenue recorded yet." />
              ) : (
                <div className="flex items-end gap-2 h-24">
                  {reports.financial_summaries.map((m) => (
                    <div key={m.month} className="flex-1 flex flex-col items-center gap-1 min-w-0">
                      <div className="w-full bg-gray-900 rounded-t relative overflow-hidden h-20" title={`${m.month}: ${fmtCurrency(m.revenue)} (${m.takedowns} takedowns)`}>
                        <div
                          className="absolute bottom-0 left-0 right-0 bg-emerald-500/70 rounded-t"
                          style={{ height: `${(m.revenue / maxRevenue) * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-gray-500 truncate w-full text-center">{m.month.slice(5)}</span>
                    </div>
                  ))}
                </div>
              )}
              {reports.financial_summaries.length > 0 && (
                <div className="mt-2 text-xs text-gray-400 text-right">
                  Total: {fmtCurrency(reports.financial_summaries.reduce((sum, m) => sum + (m.revenue || 0), 0))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const EmptyState = ({ text }) => (
  <div className="text-xs text-gray-500 italic py-4 text-center border border-gray-800 rounded-lg">
    {text}
  </div>
);

const execToneClasses = {
  amber: { border: 'border-amber-500/30', glow: 'from-amber-500/10', icon: 'text-amber-400 bg-amber-500/20' },
  rose: { border: 'border-rose-500/30', glow: 'from-rose-500/10', icon: 'text-rose-400 bg-rose-500/20' },
  emerald: { border: 'border-emerald-500/30', glow: 'from-emerald-500/10', icon: 'text-emerald-400 bg-emerald-500/20' },
  cyan: { border: 'border-cyan-500/30', glow: 'from-cyan-500/10', icon: 'text-cyan-400 bg-cyan-500/20' },
  purple: { border: 'border-purple-500/30', glow: 'from-purple-500/10', icon: 'text-purple-400 bg-purple-500/20' },
};

const ExecStat = ({ icon: Icon, tone = 'cyan', label, value, sub, loading, testId }) => {
  const t = execToneClasses[tone] || execToneClasses.cyan;
  return (
    <div
      className={`bg-gradient-to-br ${t.glow} to-transparent border ${t.border} rounded-2xl p-5`}
      data-testid={testId}
    >
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${t.icon}`}>
          <Icon size={20} />
        </div>
        {loading && <Loader2 size={14} className="text-gray-500 animate-spin" />}
      </div>
      <div className="text-3xl font-bold tabular-nums">{value}</div>
      <div className="text-sm font-medium text-gray-300 mt-1">{label}</div>
      {sub && <div className="text-xs text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
};

export default CounterfindExecutiveDashboard;
